import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, getCSSVariable, PressableBox, Row } from '@opendoor/bricks-next';
import { IconChevronLeft, IconSearch } from '@tabler/icons-react';
import NewSellerFlowAddressInput from 'components/shared/NewSellerFlowAddressInput';

interface GlassNavSearchProps {
  isVisible: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

const SearchIcon = () => (
  <Box display="flex" alignItems="center" justifyContent="center">
    <IconSearch size={18} color={getCSSVariable('$contentTertiary', 'color')} stroke={2} />
  </Box>
);

const DesktopNavSearch: React.FC<GlassNavSearchProps> = ({ isVisible }) => (
  <Box
    display="flex"
    alignItems="center"
    mx="$3"
    my="$1half"
    opacity={isVisible ? 1 : 0}
    pointerEvents={isVisible ? 'auto' : 'none'}
    maxWidth={isVisible ? '$120' : '$0'}
    $platform-web={{ transition: 'opacity 0.3s, max-width 0.4s' }}
  >
    <Box flex={1} minWidth={0} ml="$2" width={'100%'}>
      <NewSellerFlowAddressInput
        hideLabel
        inputLocation="hero-cta"
        analyticsPrefix="cosmos-glass-nav-search-desktop"
        trackingTaxonomy="new_homepage"
        placeholder="Enter home address"
        icon={<SearchIcon />}
        size="xsmall"
      />
    </Box>
  </Box>
);

const MobileNavSearch: React.FC<GlassNavSearchProps> = ({ isVisible, onExpandChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const collapseRafRef = useRef<number>(0);
  const closeButtonRef = useRef<HTMLDivElement>(null);

  const expand = useCallback(() => {
    if (typeof window !== 'undefined') {
      cancelAnimationFrame(collapseRafRef.current);
    }
    setIsExpanded(true);
    onExpandChange?.(true);
  }, [onExpandChange]);

  const collapseIfBlurred = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }
    collapseRafRef.current = requestAnimationFrame(() => {
      const active = document.activeElement;
      if (
        containerRef.current &&
        !containerRef.current.contains(active) &&
        !closeButtonRef.current?.contains(active)
      ) {
        setIsExpanded(false);
        onExpandChange?.(false);
      }
    });
  }, [onExpandChange]);

  const collapseExplicitly = useCallback(() => {
    if (typeof window !== 'undefined') {
      cancelAnimationFrame(collapseRafRef.current);
    }
    setIsExpanded(false);
    onExpandChange?.(false);
  }, [onExpandChange]);

  useEffect(() => {
    return () => {
      if (collapseRafRef.current) {
        cancelAnimationFrame(collapseRafRef.current);
      }
    };
  }, []);

  return (
    <Row
      alignItems="center"
      width="100%"
      gap="$2"
      opacity={isVisible ? 1 : 0}
      pointerEvents={isVisible ? 'auto' : 'none'}
      $platform-web={{ transition: 'opacity 0.3s ease' }}
    >
      <PressableBox
        ref={closeButtonRef}
        aria-label="Close search"
        aria-hidden={!isExpanded}
        tabIndex={isExpanded ? 0 : -1}
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        height="$11"
        backgroundColor="$transparent"
        cursor="pointer"
        analyticsName="cosmos-glass-nav-search-close"
        onPress={collapseExplicitly}
        borderWidth={0}
        p="$0"
        overflow="hidden"
        width={isExpanded ? '$7' : '$0'}
        opacity={isExpanded ? 1 : 0}
        $platform-web={{ transition: 'width 0.2s ease, opacity 0.2s ease' }}
      >
        <IconChevronLeft size={22} color={getCSSVariable('$contentPrimary', 'color')} />
      </PressableBox>

      <Box ref={containerRef} flex={1} minWidth={0} onFocus={expand} onBlur={collapseIfBlurred}>
        <NewSellerFlowAddressInput
          hideLabel
          inputLocation="hero-cta"
          analyticsPrefix="cosmos-glass-nav-search-mobile"
          trackingTaxonomy="new_homepage"
          placeholder="Enter home address"
          icon={<SearchIcon />}
          size="xsmall"
        />
      </Box>
    </Row>
  );
};

export const GlassNavSearch: React.FC<GlassNavSearchProps> = ({ isVisible, onExpandChange }) => {
  if (onExpandChange) {
    return <MobileNavSearch isVisible={isVisible} onExpandChange={onExpandChange} />;
  }
  return <DesktopNavSearch isVisible={isVisible} />;
};
